import type { LeadFlowContractRuleIssue } from "@homeinstead/forms/types";
import type { LeadFlowDocument } from "../types";

import {
  getLeadFlowContractIssues,
  isLeadFormDeliveryContext,
} from "@homeinstead/forms/lead-form";

/** One reason for the debug output, naming every missing field together. */
function describeContractIssues(
  issues: LeadFlowContractRuleIssue[],
): string | null {
  const [issue] = issues;
  if (!issue) {
    return null;
  }

  switch (issue.code) {
    case "duplicateField":
      return `"${issue.fieldName}" is asked more than once.`;
    case "injectedPostalCode":
      return "an office-local flow asks for a postal code.";
    case "missingField":
      return `required fields are never asked: ${issues
        .flatMap((missing) =>
          missing.code === "missingField" ? [missing.fieldName] : [],
        )
        .join(", ")}.`;
  }
}

/**
 * Why a resolved flow can't run against the `careSeeker` contract, or null
 * when it can. Studio validation enforces the same rules on publish; this
 * re-checks the published content the page actually received, so a flow
 * that slipped past validation fails closed instead of submitting a partial
 * lead.
 */
function getContractIssue(leadFlow: LeadFlowDocument): string | null {
  const { deliveryContext, formType } = leadFlow;

  if (formType !== "careSeeker") {
    return `form type "${formType ?? "unset"}" is not careSeeker.`;
  }
  if (!isLeadFormDeliveryContext(deliveryContext)) {
    return `delivery context "${deliveryContext ?? "unset"}" is not supported.`;
  }

  return describeContractIssues(
    getLeadFlowContractIssues({
      deliveryContext,
      formType,
      steps: (leadFlow.steps ?? []).map((step) => ({
        items: step._type === "leadFlowStep" ? step.items : [],
      })),
    }),
  );
}

export { getContractIssue };
